import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Paperclip } from '../../icons';
// 화면 머리 — 근태 · 메일과 같은 정본 A3 (2026-09-08).
import { PageHeader } from '../../components/shared/PageHeader';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import { listDocuments } from '../../api/approval';
import { listDocTypes, type DocTypeDto } from '../../api/approval-doc-type';
import type { ApprovalDocumentSummary } from '../../types/approval';
// 결재 상태 표 한 벌(2026-10-18 · web 레인 W11b) — 완료 문서 상태는 칩이 아니라 회색 글
import { approvalStatusMark } from '../../lib/approval-status';
import './box/approval-box-status.css';
import { Button } from '../../components/shared/Button/Button';
import './ApprovalHome.css';

/**
 * 결재 홈 — 진행 중인 것 하나, 그 아래 표 둘 (2026-09-07 · 시안 A2 · 정본 D12).
 *
 * ── 무엇이 바뀌나 ────────────────────────────────────────────────────────
 * 종전 홈은 **숫자 카드 넷**이었다 — 「내가 결재할 것 4건」처럼 **세기만** 하고 무엇인지는
 * 안 알려 준다. 세어 놓고 다시 함을 열어야 하니 한 번 더 누르게 만드는 화면이었다.
 * 그 자리에 **문서 자체**를 놓는다.
 *
 * ── ★★★ 나누지 않는다 (사용자 지정) ─────────────────────────────────────
 * *「내가 결재, 내가 올린 것은 그룹을 나누지 말고 할 것」*
 * 나누면 같은 화면을 두 번 훑게 되고, 정작 급한 것이 두 번째 묶음에 있을 수 있다.
 * 한 묶음으로 두되 **단추가 다르다** — 내 차례면 「결재하기」, 아니면 「문서 열기」.
 * 그것이 묶음을 나누지 않고도 **할 일과 지켜볼 일**을 가른다(정본 D12).
 *
 * ── ★★ 「결재상태」 칸은 상태를 안 적는다 ────────────────────────────────
 * 진행 문서는 전부 `IN_PROGRESS` 라, 그 칸에 「진행중」만 적으면 **모든 줄이 같은 값**이 된다
 * (정본 P3 「같은 값만 나오는 칸은 칸이 아니다」). 그래서 **「내 차례」 또는 「n/N · 누구 대기」**
 * 를 적는다 — 상태가 아니라 **지금 어디까지 왔나**가 이 자리에서 알고 싶은 것이다.
 *
 * ── ★ 못 읽은 것은 «—» 이지 «0» 이 아니다 ───────────────────────────────
 * 0 으로 채우면 「할 일이 없다」는 거짓말이 된다.
 */

/** 카드에 세우는 최대 건수. 나머지는 아래 표가 전부 담는다. */
const CARD_MAX = 6;

const isRunning = (d: ApprovalDocumentSummary) => d.status === 'IN_PROGRESS';

/** 기안일 — 상신한 날. 임시보관은 아직 안 냈으므로 만든 날로 대신한다. */
const draftedOn = (d: ApprovalDocumentSummary) => d.draftedAt ?? d.createdAt;

/** `2026-09-05` — 표에 서는 꼴(정본 「표기」 절). */
function ymd(iso: string): string {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '—';
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

/** 며칠째 — 「사람 이름과 며칠째가 진행률을 대신한다」(정본 D12). */
function daysSince(iso: string): number | null {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000)) + 1;
}

export function ApprovalHome(): JSX.Element {
  const navigate = useNavigate();
  const [incoming, setIncoming] = useState<ApprovalDocumentSummary[] | null>(null);
  const [drafted, setDrafted] = useState<ApprovalDocumentSummary[] | null>(null);
  /** 양식 코드 → 회사가 지은 이름. 못 읽으면 코드를 그대로 그린다(지어내지 않는다). */
  const [types, setTypes] = useState<DocTypeDto[]>([]);

  useEffect(() => {
    let alive = true;
    // 조각마다 따로 — 하나가 죽어도 나머지는 뜬다.
    void listDocuments('incoming', { limit: 50 })
      .then((r) => { if (alive) setIncoming(r.items); })
      .catch(() => { if (alive) setIncoming(null); });
    void listDocuments('drafted', { limit: 50 })
      .then((r) => { if (alive) setDrafted(r.items); })
      .catch(() => { if (alive) setDrafted(null); });
    void listDocTypes()
      .then((t) => { if (alive) setTypes(t); })
      .catch(() => { if (alive) setTypes([]); });
    return () => { alive = false; };
  }, []);

  const typeName = useMemo(() => {
    const m = new Map(types.map((t) => [t.code, t.name]));
    return (code: string) => m.get(code) ?? code;
  }, [types]);

  /**
   * ★★★ **한 묶음이다.** 내가 결재할 것과 내가 올린 것을 합치고, 같은 문서가 양쪽에 있으면
   *    한 번만 센다(내가 올린 문서에 내가 참조로 걸린 경우 등).
   */
  const failed = incoming === null && drafted === null;
  const all = useMemo(() => {
    const byId = new Map<string, ApprovalDocumentSummary>();
    for (const d of [...(incoming ?? []), ...(drafted ?? [])]) byId.set(d.id, d);
    return [...byId.values()].sort(
      (a, b) => new Date(draftedOn(b)).getTime() - new Date(draftedOn(a)).getTime(),
    );
  }, [incoming, drafted]);

  const running = useMemo(() => all.filter(isRunning), [all]);
  const done = useMemo(() => all.filter((d) => !isRunning(d) && d.status !== 'DRAFT'), [all]);

  /** 내 차례인가 — 서버가 준 판정을 그대로 쓴다(위임받은 것도 참). */
  const isMine = (d: ApprovalDocumentSummary) => d.waiting?.mine === true;

  /** 「지금 누가 들고 있나」 — 옛 서버는 이 값을 안 준다(그때는 단계 숫자만 적는다). */
  const whoHas = (d: ApprovalDocumentSummary): string => {
    if (d.waiting?.mine) return '내 차례';
    if (d.waiting) return `${d.waiting.name ?? '(지워진 계정)'} 대기`;
    return `${d.currentStep}/${d.totalSteps}단계`;
  };

  const openDoc = (id: string) => navigate(`/approval/doc/${id}`);

  return (
    <div className="ahome">
      {/*
        ★★★ 화면 머리 — **정본 `PageHeader`(A3)** (2026-09-08 사용자 지적).
           손으로 짠 `<h1>` + `<p>` 라, 근태 · 메일에는 있는 **경로 · 도움말**이 없었다.
        ★ 제목은 **메뉴 이름과 같아야 한다**(`appr-menu.test.ts`) — 그 규칙은 그대로다.
      */}
      <PageHeader
        className="ahome__head"
        breadcrumb={crumbsFor('/approval')}
        scope={scopeFor('/approval')}
        title="결재 홈"
        description="지금 진행 중인 것과, 끝난 것."
        help={{
          title: '이 화면의 규칙',
          body: (
            <>
              <p><b>진행 중</b>은 내가 올렸거나 내 차례인 문서입니다. 내 차례가 아니면 여기 없습니다.</p>
              <p>문서를 올리는 곳은 왼쪽 <b>기안하기</b>이고, 지나간 것은 <b>결재함</b>에서 봅니다.</p>
              <p>결재선은 <b>기안할 때 고칠 수 있습니다</b> — 회사가 정한 기본 결재선이 미리 놓입니다.</p>
            </>
          ),
        }}
      />

      {failed && (
        <p className="ahome__err" role="alert">
          문서를 불러오지 못했습니다 — 왼쪽 함에서 직접 열 수 있습니다.
        </p>
      )}

      {/* ══════════ 진행 중 — 한 묶음 ══════════ */}
      <section className="ahome__sec" aria-label="진행 중">
        <div className="ahome__sech">
          <h2>진행 중</h2>
          {!failed && <span className="ahome__n">{running.length}</span>}
          <span className="ahome__sp" />
          {running.length > CARD_MAX && (
            <span className="ahome__more">아래 표에 전부 있습니다</span>
          )}
        </div>

        {!failed && running.length === 0 ? (
          <p className="ahome__empty">진행 중인 문서가 없습니다.</p>
        ) : (
          <div className="ahome__cards">
            {running.slice(0, CARD_MAX).map((d) => {
              const days = daysSince(draftedOn(d));
              const mine = isMine(d);
              return (
                <article key={d.id} className="ahome__card">
                  <div className="ahome__ct">
                    <span className="ahome__form">{typeName(d.type)}</span>
                    {d.isUrgent && <span className="ahome__urg">긴급</span>}
                  </div>
                  <button
                    type="button"
                    className="ahome__ctitle"
                    onClick={() => openDoc(d.id)}
                    title={d.title}
                  >
                    {d.title}
                  </button>
                  <div className="ahome__cwho">
                    {ymd(draftedOn(d)).slice(5)} 기안{days !== null ? ` · ${days}일째` : ''}
                  </div>
                  {/* 막대는 「얼마나 남았나」만 거든다 — 진행률을 대신하는 것은 위의 사람과 날짜다. */}
                  <div className="ahome__prog" aria-hidden>
                    {Array.from({ length: Math.max(1, d.totalSteps) }, (_, i) => (
                      <i
                        key={i}
                        className={i + 1 < d.currentStep ? 'is-ok' : i + 1 === d.currentStep ? 'is-now' : ''}
                      />
                    ))}
                  </div>
                  <div className="ahome__cf">
                    <span className="ahome__cst">
                      {mine ? <b>내 차례</b> : whoHas(d)} · {d.currentStep}/{d.totalSteps}
                    </span>
                    {/* ★★ 단추가 사람마다 다르다 — 묶음을 나누지 않고 할 일을 가르는 장치다. */}
                    <Button
                      variant={mine ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={() => openDoc(d.id)}
                    >
                      {mine ? '결재하기' : '문서 열기'}
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* ══════════ 진행 문서 ══════════ */}
      <DocTable
        label="진행 문서"
        rows={running}
        count={failed ? null : running.length}
        typeName={typeName}
        onOpen={openDoc}
        whoHas={whoHas}
        showDocNo={false}
        empty="진행 중인 문서가 없습니다."
      />

      {/* ══════════ 완료 문서 ══════════ */}
      <DocTable
        label="완료 문서"
        rows={done}
        count={failed ? null : done.length}
        typeName={typeName}
        onOpen={openDoc}
        whoHas={whoHas}
        showDocNo
        empty="끝난 문서가 없습니다."
        aside={<Link className="ahome__more" to="/approval/drafts">기안 문서함에서 더 보기</Link>}
      />
    </div>
  );
}

interface TableProps {
  label: string;
  rows: ApprovalDocumentSummary[];
  count: number | null;
  typeName: (code: string) => string;
  onOpen: (id: string) => void;
  whoHas: (d: ApprovalDocumentSummary) => string;
  /** 완료 문서에만 문서번호 칸이 선다 — 진행 중에는 대개 이미 있지만 쓸 일이 없다. */
  showDocNo: boolean;
  empty: string;
  aside?: JSX.Element;
}

function DocTable({
  label, rows, count, typeName, onOpen, whoHas, showDocNo, empty, aside,
}: TableProps): JSX.Element {
  /**
   * ★ 첨부 칸은 **서버가 개수를 줄 때만** 그린다.
   *   옛 서버는 이 값을 안 준다(`undefined`) — 그때 「—」로 그리면 「첨부가 없다」는 거짓말이 된다.
   */
  const hasAttachInfo = rows.some((d) => typeof d.attachmentCount === 'number');

  return (
    <section className="ahome__sec" aria-label={label}>
      <div className="ahome__sech">
        <h2>{label}</h2>
        {count !== null && <span className="ahome__n">{count}</span>}
        <span className="ahome__sp" />
        {aside}
      </div>

      {rows.length === 0 ? (
        <p className="ahome__empty">{empty}</p>
      ) : (
        <div className="ahome__tblwrap">
          <table className="ahome__tbl">
            <thead>
              <tr>
                <th scope="col" className="ahome__num" style={{ width: 104 }}>기안일</th>
                <th scope="col" style={{ width: 124 }}>결재양식</th>
                <th scope="col" style={{ width: 52 }}>긴급</th>
                <th scope="col">제목</th>
                {hasAttachInfo && <th scope="col" style={{ width: 60 }}>첨부</th>}
                {showDocNo && <th scope="col" className="ahome__num" style={{ width: 150 }}>문서번호</th>}
                <th scope="col" style={{ width: showDocNo ? 84 : 150 }}>결재상태</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} onClick={() => onOpen(d.id)} className="ahome__row">
                  <td className="ahome__num ahome__mut">{ymd(draftedOn(d))}</td>
                  <td>{typeName(d.type)}</td>
                  <td>{d.isUrgent && <span className="ahome__urg">긴급</span>}</td>
                  <td className="ahome__ttl">
                    {/* 행 전체가 문이지만 키보드로도 닿아야 한다 — 제목이 그 문이다. */}
                    <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(d.id); }}>
                      {d.title}
                    </button>
                  </td>
                  {hasAttachInfo && (
                    <td>
                      {d.attachmentCount ? (
                        <span className="ahome__clip">
                          <Paperclip size={13} aria-hidden />{d.attachmentCount}
                        </span>
                      ) : <span className="ahome__mut">—</span>}
                    </td>
                  )}
                  {showDocNo && (
                    <td className="ahome__num">
                      {/* 번호는 **상신할 때** 매긴다 — 회수·옛 문서에는 없다. 지어내지 않는다. */}
                      {d.docNo ?? <span className="ahome__mut">—</span>}
                    </td>
                  )}
                  <td>
                    {d.status === 'IN_PROGRESS' ? (
                      <span className={`ahome__st${d.waiting?.mine ? ' is-mine' : ''}`}>
                        {d.waiting?.mine ? '내 차례' : `${d.currentStep}/${d.totalSteps} · ${whoHas(d)}`}
                      </span>
                    ) : (
                      <span className="ahome__st">{approvalStatusMark(d.status, false).label}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
